import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiGift, FiUsers, FiUser, FiImage, FiX } = FiIcons;

const AppPreview = () => {
  return (
    <section className="py-20 px-6 bg-gradient-to-br from-rose-light to-white">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-rose-primary to-rose-secondary bg-clip-text text-transparent">
              Beautiful Design
            </span>
            <br />
            <span className="text-gray-800">Meets Powerful Features</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience the elegant interface designed to make gift list creation and sharing a delightful experience.
          </p>
        </motion.div>

        {/* App Screenshots Mockup */}
        <div className="flex flex-col lg:flex-row items-center justify-center space-y-8 lg:space-y-0 lg:space-x-12">
          {/* Phone 1: My Wishlist */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="w-64 h-[500px] bg-gradient-to-br from-rose-primary to-rose-secondary rounded-[3rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-white rounded-[2.5rem] flex flex-col overflow-hidden relative">
                <div className="p-4 text-center border-b">
                  <h3 className="font-bold text-lg">My Wishlist</h3>
                  <p className="text-xs text-gray-500">9 items</p>
                </div>
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <div className="w-full h-20 bg-gray-200 rounded-lg mb-2"></div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">Glow in the Dark Blanket</p>
                      </div>
                      <span className="bg-rose-100 text-rose-500 text-xs px-2 py-0.5 rounded-full font-medium">Want</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-xl">
                    <div className="w-full h-20 bg-gray-200 rounded-lg mb-2"></div>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">Ninja CREAMI Ice Cream Maker</p>
                        <p className="text-gray-600 text-sm font-bold">$220.99</p>
                      </div>
                      <span className="bg-rose-100 text-rose-500 text-xs px-2 py-0.5 rounded-full font-medium">Want</span>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-20 right-4 w-12 h-12 bg-rose-primary rounded-full flex items-center justify-center text-white shadow-lg">
                  <SafeIcon icon={FiPlus} className="text-2xl" />
                </div>
                <div className="grid grid-cols-4 gap-2 p-2 border-t mt-auto">
                  <div className="text-center text-rose-primary">
                    <SafeIcon icon={FiGift} className="mx-auto text-xl" />
                    <span className="text-xs">My List</span>
                  </div>
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiUsers} className="mx-auto text-xl" />
                    <span className="text-xs">Discover</span>
                  </div>
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiUsers} className="mx-auto text-xl" />
                    <span className="text-xs">Groups</span>
                  </div>
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiUser} className="mx-auto text-xl" />
                    <span className="text-xs">Profile</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Phone 2: Add Item */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="relative lg:mt-12"
          >
            <div className="w-64 h-[500px] bg-gradient-to-br from-rose-secondary to-rose-accent rounded-[3rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-white rounded-[2.5rem] flex flex-col overflow-hidden">
                <div className="p-4 flex items-center justify-between border-b">
                  <SafeIcon icon={FiX} className="text-xl text-gray-600" />
                  <h3 className="font-bold text-lg">Add Item</h3>
                  <button className="text-sm bg-rose-primary text-white px-3 py-1 rounded-full font-medium">Save</button>
                </div>
                <div className="p-3 space-y-3 overflow-y-auto flex-1 text-xs">
                  <div>
                    <label className="font-medium text-gray-600">Product URL (Optional)</label>
                    <input type="text" placeholder="https://example.com/product" className="w-full p-2 border rounded-lg mt-1 text-xs" />
                  </div>
                  <div className="border border-dashed rounded-lg p-4 text-center text-gray-400">
                    <SafeIcon icon={FiImage} className="mx-auto text-2xl mb-1" />
                    <span>Add Photo</span>
                  </div>
                  <div>
                    <label className="font-medium text-gray-600">Product Name / Description</label>
                    <input type="text" placeholder="e.g., Wireless Headphones" className="w-full p-2 border rounded-lg mt-1 text-xs" />
                  </div>
                  <div>
                    <label className="font-medium text-gray-600">Type *</label>
                    <div className="flex space-x-2 mt-1">
                      <button className="flex-1 border-2 border-rose-primary bg-rose-primary text-white py-2 rounded-lg font-medium">Need</button>
                      <button className="flex-1 border border-gray-300 py-2 rounded-lg font-medium">Want</button>
                    </div>
                  </div>
                  <div>
                    <label className="font-medium text-gray-600">Urgency Level *</label>
                    <div className="grid grid-cols-4 gap-2 mt-1">
                      <button style={{
      fontSize: '10px'
    }} className="border border-gray-300 py-1 rounded-full">Low</button>
                      <button style={{
      fontSize: '10px'
    }}className="border border-gray-300 py-1 rounded-full">Medium</button>
                      <button style={{
      fontSize: '10px'
    }}className="border-2 border-rose-primary text-rose-primary py-1 rounded-full bg-rose-50">High</button>
                      <button style={{
      fontSize: '10px'
    }} className="border border-gray-300 py-1 rounded-full">Urgent</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Phone 3: Gift Exchange Groups */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="w-64 h-[500px] bg-gradient-to-br from-rose-accent to-rose-primary rounded-[3rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-white rounded-[2.5rem] flex flex-col overflow-hidden">
                <div className="p-4 text-center border-b">
                  <h3 className="font-bold text-lg">Gift Exchange Groups</h3>
                  <p className="text-xs text-gray-500">Share wish lists with your groups</p>
                </div>
                <div className="p-4 space-y-4 flex-1">
                  <div className="bg-white p-3 rounded-xl shadow-md border">
                    <p className="font-bold text-sm">Coleman Christmas</p>
                    <p className="text-xs text-gray-500">$25 limit family secret Santa</p>
                    <p className="text-xs text-gray-400 mt-1">1 member</p>
                  </div>
                  <div className="bg-white p-3 rounded-xl shadow-md border">
                    <p className="font-bold text-sm">Stuff</p>
                    <p className="text-xs text-gray-500">Yee</p>
                    <p className="text-xs text-gray-400 mt-1">2 members</p>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3 border-t">
                  <button style={{
      fontSize: '10px'
    }} className="border-2 border-rose-primary text-rose-primary py-2 rounded-full font-medium">Join Group</button>
                  <button style={{
      color: 'rgb(255, 255, 255)',
      fontSize: '10px'
    }} className="bg-rose-primary text-white py-2 rounded-full font-medium">Create Group</button>
                </div>
                <div className="grid grid-cols-4 gap-2 p-2 border-t">
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiGift} className="mx-auto text-xl" />
                    <span className="text-xs">My List</span>
                  </div>
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiUsers} className="mx-auto text-xl" />
                    <span className="text-xs">Discover</span>
                  </div>
                  <div className="text-center text-rose-primary">
                    <SafeIcon icon={FiUsers} className="mx-auto text-xl" />
                    <span className="text-xs">Groups</span>
                  </div>
                  <div className="text-center text-gray-400">
                    <SafeIcon icon={FiUser} className="mx-auto text-xl" />
                    <span className="text-xs">Profile</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AppPreview;